import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { refreshSession } from '@/api/services/auth.api';
import { requestSessionFromOpenTabs } from '@/api/session-share';
import { getAccessToken, setAccessToken } from '@/api/token';
import { Spinner } from '@/components/ui/spinner';
import { rememberReturnTo } from '@/lib/auth-return-to';
import { PUBLIC_ROUTE } from '@/router/constant/routes';

/**
 * Signed-in pages only. With a token this renders straight away, exactly as it
 * always has. Without one — typically a NEW tab when "Remember me" was off,
 * since that token lives in per-tab `sessionStorage` — it first asks the app's
 * open tabs for their session (see `session-share.ts`), then tries the httpOnly
 * refresh cookie every tab shares, and only sends the person to the login page
 * when neither has a session. Signing in then continues to the page they asked
 * for rather than the home page.
 */
async function findExistingSession(): Promise<string | null> {
    const shared = await requestSessionFromOpenTabs();
    if (shared) return shared;
    // No open tab answered (it was closed, or the browser throttled it) — the
    // refresh cookie outlives any single tab, so it can still sign this one in.
    return refreshSession().catch(() => null);
}

export function ProtectedRoute() {
    const stored = getAccessToken();
    const location = useLocation();
    // `undefined` = still asking; `null` = no tab or cookie had a session.
    const [shared, setShared] = useState<string | null | undefined>(undefined);

    useEffect(() => {
        if (stored) return;
        let active = true;
        void findExistingSession().then((token) => {
            if (!active) return;
            // `false`: kept in this tab's sessionStorage, so a session that was
            // not remembered still ends when the last tab closes.
            if (token) setAccessToken(token, false);
            setShared(token);
        });
        return () => {
            active = false;
        };
    }, [stored]);

    // A shared session is stored before `shared` is set, so the re-render finds
    // it here; a token cleared later (sign-out) falls through to the login page.
    if (stored) return <Outlet />;
    if (shared !== undefined) {
        // Signing in continues to this page rather than the home page.
        rememberReturnTo(`${location.pathname}${location.search}${location.hash}`);
        return <Navigate to={PUBLIC_ROUTE.LOGIN} replace />;
    }
    return (
        <div className="flex min-h-svh items-center justify-center" aria-busy="true">
            <Spinner className="text-primary size-6" />
        </div>
    );
}
